import { baseApi } from "@/services/api";

import { Release, ReleaseActivePointer } from "./types";

const apiWithTags = baseApi.enhanceEndpoints({});

interface GetReleasesQueryArgs {
  params: Record<string, string>;
}

/**
 * V2 Device Fleet release management.
 * Paths are relative to the V2 admin API base (admin-*-api-v2.krosskast.com)
 * and live under /v1/sa/device-fleet once domain-routing is enabled.
 * Until then the staging execute-api host for kk-admin-sa-device-fleet may be
 * used for verification only.
 */
export const releasesApi = apiWithTags.injectEndpoints({
  endpoints: (builder) => ({
    getReleases: builder.query<
      { items: Release[]; active: ReleaseActivePointer | null; stage: string },
      GetReleasesQueryArgs
    >({
      query: ({ params }) => {
        const searchParams = Object.keys(params || {}).length
          ? new URLSearchParams(params).toString()
          : undefined;
        const base = "v1/sa/device-fleet/releases";
        return searchParams ? `${base}?${searchParams}` : base;
      },
      providesTags: ["Releases"],
    }),
    createRelease: builder.mutation({
      query: (data) => ({
        url: "v1/sa/device-fleet/releases",
        method: "POST",
        body: data,
      }),
      invalidatesTags: ["Releases"],
    }),
    activateRelease: builder.mutation({
      query: (id: string) => ({
        url: `v1/sa/device-fleet/releases/${encodeURIComponent(id)}/activate`,
        method: "POST",
        body: {},
      }),
      invalidatesTags: ["Releases"],
    }),
    getSignedUploadUrl: builder.mutation<
      { url: string; key: string; expiresIn: number },
      { filename: string }
    >({
      query: ({ filename }) => ({
        url: "v1/sa/device-fleet/release/sign",
        method: "POST",
        body: { filename },
      }),
    }),
  }),
});

export const {
  useCreateReleaseMutation,
  useActivateReleaseMutation,
  useGetReleasesQuery,
  useGetSignedUploadUrlMutation,
} = releasesApi;
